// CreditsPage.js — third-party icon attribution (/credits).
//
// The icon-maker microservice composites app icons from game-icons.net glyphs
// (CC BY 3.0 — attribution required; a site credits page is the CC-endorsed
// mechanism). The authoritative ledger of which glyphs/authors are vendored
// lives in mchatai-source, so this page fetches it at runtime — new glyphs
// added by a ledger PR show up here with zero web redeploy.
//
// If the fetch fails (offline, ledger not yet pushed) the page still renders
// the static entries plus an "unavailable" note — never a broken page.

import React, { useEffect, useState } from 'react';
import './App.css';

const LEDGER_URL =
  'https://raw.githubusercontent.com/LawrenceNorman/mchatai-source/main/microservices/icon-maker/assets/game_icons/LEDGER.json';

// Group ledger icons by author so each artist gets one line of credit.
function groupByAuthor(icons) {
  const byAuthor = new Map();
  for (const icon of icons) {
    const author = icon.author || 'Unknown';
    if (!byAuthor.has(author)) byAuthor.set(author, []);
    byAuthor.get(author).push(icon);
  }
  return [...byAuthor.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

const CreditsPage = () => {
  const [ledger, setLedger] = useState(null);
  const [ledgerError, setLedgerError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(LEDGER_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        if (!json || !Array.isArray(json.icons)) throw new Error('bad ledger shape');
        setLedger(json);
      })
      .catch(() => {
        if (!cancelled) setLedgerError(true);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className='policy'>
      <h1>Credits</h1>
      <p>Some app icons on this site are composited from third-party icon artwork:</p>

      <h2>game-icons.net</h2>
      {ledger ? (
        <>
          <p>
            Icon glyphs from{' '}
            <a href="https://game-icons.net" target="_blank" rel="noopener noreferrer">game-icons.net</a>,
            licensed under{' '}
            <a href={ledger.license_url || 'https://creativecommons.org/licenses/by/3.0/'} target="_blank" rel="noopener noreferrer">
              {ledger.license || 'CC BY 3.0'}
            </a>.
            {ledger.modified ? <> Modifications: {ledger.modified}.</> : null}
          </p>
          <ul>
            {groupByAuthor(ledger.icons).map(([author, icons]) => (
              <li key={author}>
                <strong>{author}</strong>:{' '}
                {icons.map((icon, i) => (
                  <React.Fragment key={icon.id || i}>
                    {i > 0 && ', '}
                    <a href={icon.source_url} target="_blank" rel="noopener noreferrer">{icon.id}</a>
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
        </>
      ) : ledgerError ? (
        <p style={{ color: '#6b7280' }}>
          Icon glyphs from{' '}
          <a href="https://game-icons.net" target="_blank" rel="noopener noreferrer">game-icons.net</a>,
          licensed under{' '}
          <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener noreferrer">CC BY 3.0</a>.
          {' '}The game-icons.net attribution ledger is unavailable right now — per-icon author credits will appear here once it loads.
        </p>
      ) : (
        <p style={{ color: '#9ca3af' }}>Loading attribution ledger…</p>
      )}

      <h2>Material Icons</h2>
      <p>
        <a href="https://fonts.google.com/icons" target="_blank" rel="noopener noreferrer">Material Icons</a> by Google,
        licensed under the{' '}
        <a href="https://www.apache.org/licenses/LICENSE-2.0" target="_blank" rel="noopener noreferrer">Apache License 2.0</a>.
      </p>

      <h2>Emoji</h2>
      <p>Emoji shown across the site render via your device's platform emoji font.</p>
    </div>
  );
};

export default CreditsPage;
